import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ScaleIcon,
  CurrencyDollarIcon,
  ClockIcon,
  LinkIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import apiClient from '../services/api';

interface TrademarkClass {
  class_number: number;
  name: string;
  description: string;
  relevance?: string;
  reason?: string;
}

interface SimilarityScore {
  phonetic: number;
  visual: number;
  conceptual?: number;
  overall: number;
}

interface SearchResult {
  serial_number: string;
  registration_number: string | null;
  mark: string;
  status: string;
  owner: string;
  filing_date: string;
  classes: number[];
  similarity_scores: SimilarityScore;
  goods_services: string;
  source?: string;
  warning?: string;
}

interface OfficialSearchLink {
  name: string;
  url: string;
  description: string;
  search_tip: string;
}

interface AvailabilityAssessment {
  status: string;
  assessment: string;
  high_risk_count: number;
  medium_risk_count: number;
}

interface SearchResponse {
  search_term: string;
  country: string;
  country_name: string;
  search_type: string;
  state?: string;
  total_results: number;
  results: SearchResult[];
  official_search_links: OfficialSearchLink[];
  availability_assessment: AvailabilityAssessment;
  disclaimer: string;
}

interface TrademarkCountry {
  code: string;
  name: string;
  office: string;
  search_url: string;
  has_api: boolean;
  regions_label?: string;
  supports_state_search?: boolean;
}

interface TrademarkState {
  code: string;
  name: string;
  office: string;
  url: string;
}

interface FeeInfo {
  type: string;
  per_class: number;
  description: string;
  requirements: string[];
}

interface AdditionalFee {
  action: string;
  fee: number;
}

interface FeesResponse {
  application_fees: FeeInfo[];
  additional_fees: AdditionalFee[];
  notes: string[];
  last_updated: string;
}

type TabType = 'search' | 'classes' | 'similarity' | 'application' | 'fees';

export const TrademarkSearch: React.FC = () => {
  const { t } = useTranslation();
  const { accessToken } = useAuthStore();

  // State
  const [activeTab, setActiveTab] = useState<TabType>('search');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchMark, setSearchMark] = useState('');
  const [goodsServices, setGoodsServices] = useState('');
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedState, setSelectedState] = useState('');
  const [searchType, setSearchType] = useState<'federal' | 'state' | 'both'>('federal');
  const [countries, setCountries] = useState<TrademarkCountry[]>([]);
  const [states, setStates] = useState<TrademarkState[]>([]);

  // Class suggestion state
  const [classDescription, setClassDescription] = useState('');
  const [suggestedClasses, setSuggestedClasses] = useState<TrademarkClass[]>([]);
  const [allClasses, setAllClasses] = useState<TrademarkClass[]>([]);
  const [showAllClasses, setShowAllClasses] = useState(false);

  // Similarity analysis state
  const [mark1, setMark1] = useState('');
  const [mark2, setMark2] = useState('');
  const [similarityResult, setSimilarityResult] = useState<any>(null);

  // Fees state
  const [feesData, setFeesData] = useState<FeesResponse | null>(null);

  // Application guidance state
  const [applicationData, setApplicationData] = useState<any>(null);
  const [applicationForm, setApplicationForm] = useState({
    mark: '',
    mark_type: 'standard',
    goods_services: '',
    classes: [] as number[],
    owner_name: '',
    owner_address: '',
    owner_type: 'individual',
    use_basis: 'intent_to_use',
  });

  // Load countries and classes on mount
  useEffect(() => {
    loadAllClasses();
    loadCountries();
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (selectedCountry === 'US') {
      loadStates();
    } else {
      setStates([]);
      setSelectedState('');
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/trademark-search/countries', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setCountries(response.data.countries);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadStates = async () => {
    try {
      const response = await apiClient.get('/trademark-search/states?country=US', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setStates(response.data.states);
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  };

  const loadAllClasses = async () => {
    try {
      const response = await apiClient.get('/trademark-search/classes', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setAllClasses(response.data.classes);
    } catch (err) {
      console.error('Failed to load classes:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchMark.trim()) {
      setError(t('trademark:errors.enterMark'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        '/trademark-search/search',
        {
          mark: searchMark,
          goods_services: goodsServices || undefined,
          classes: selectedClasses.length > 0 ? selectedClasses : undefined,
          country: selectedCountry,
          state: selectedState || undefined,
          search_type: searchType,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setSearchResults(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('trademark:errors.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestClasses = async () => {
    if (!classDescription.trim()) {
      setError(t('trademark:errors.enterDescription'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        '/trademark-search/suggest-classes',
        { goods_services: classDescription },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setSuggestedClasses(response.data.suggested_classes);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('trademark:errors.suggestionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeSimilarity = async () => {
    if (!mark1.trim() || !mark2.trim()) {
      setError(t('trademark:errors.enterBothMarks'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        '/trademark-search/analyze-similarity',
        { mark1, mark2 },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setSimilarityResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('trademark:errors.analysisFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFees = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/trademark-search/filing-fees', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setFeesData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('trademark:errors.feesFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGetApplicationGuidance = async () => {
    if (!applicationForm.mark || !applicationForm.goods_services || applicationForm.classes.length === 0) {
      setError(t('trademark:errors.completeForm'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        '/trademark-search/application-guidance',
        applicationForm,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setApplicationData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('trademark:errors.guidanceFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getSimilarityColor = (score: number): string => {
    if (score >= 0.7) return 'text-red-600 dark:text-red-400';
    if (score >= 0.4) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      high_risk: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      moderate_risk: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      low_risk: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      likely_available: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const renderTabs = () => (
    <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
      <nav className="-mb-px flex space-x-4 overflow-x-auto">
        {[
          { id: 'search', label: t('trademark:tabs.search'), icon: MagnifyingGlassIcon },
          { id: 'classes', label: t('trademark:tabs.classes'), icon: DocumentTextIcon },
          { id: 'similarity', label: t('trademark:tabs.similarity'), icon: ScaleIcon },
          { id: 'application', label: t('trademark:tabs.application'), icon: DocumentDuplicateIcon },
          { id: 'fees', label: t('trademark:tabs.fees'), icon: CurrencyDollarIcon },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id as TabType);
              if (tab.id === 'fees' && !feesData) handleLoadFees();
            }}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            <tab.icon className="h-5 w-5 mr-2" />
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );

  const renderSearchTab = () => (
    <div className="space-y-6">
      {/* Search Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('trademark:search.title')}
        </h3>
        <div className="space-y-4">
          {/* Jurisdiction Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('trademark:search.countryLabel')}
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedCountry === 'US' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('trademark:search.searchTypeLabel')}
                  </label>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as 'federal' | 'state' | 'both')}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="federal">{t('trademark:search.searchTypes.federal')}</option>
                    <option value="state">{t('trademark:search.searchTypes.state')}</option>
                    <option value="both">{t('trademark:search.searchTypes.both')}</option>
                  </select>
                </div>
                {(searchType === 'state' || searchType === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('trademark:search.stateLabel')}
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('trademark:search.selectState')}</option>
                      {states.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:search.markLabel')} *
            </label>
            <input
              type="text"
              value={searchMark}
              onChange={(e) => setSearchMark(e.target.value)}
              placeholder={t('trademark:search.markPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:search.goodsServicesLabel')}
            </label>
            <textarea
              value={goodsServices}
              onChange={(e) => setGoodsServices(e.target.value)}
              placeholder={t('trademark:search.goodsServicesPlaceholder')}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !searchMark.trim()}
            className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            )}
            {t('trademark:search.searchButton')}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="space-y-4">
          {/* Availability Assessment */}
          <div className={`rounded-lg p-4 ${getStatusBadge(searchResults.availability_assessment.status)}`}>
            <div className="flex items-start">
              {searchResults.availability_assessment.status === 'likely_available' ? (
                <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
              ) : searchResults.availability_assessment.status === 'high_risk' ? (
                <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
              ) : (
                <InformationCircleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
              )}
              <div>
                <h4 className="font-semibold">{t(`trademark:assessment.${searchResults.availability_assessment.status}`)}</h4>
                <p className="text-sm mt-1">{searchResults.availability_assessment.assessment}</p>
              </div>
            </div>
          </div>

          {/* Results List */}
          {searchResults.results.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t('trademark:search.resultsTitle', { count: searchResults.total_results })}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('trademark:search.wellKnownMarksNote')}
                </p>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {searchResults.results.map((result, idx) => (
                  <div key={idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{result.mark}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            result.status === 'REGISTERED'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : result.status === 'WELL-KNOWN MARK'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}>
                            {result.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{result.owner}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {t('trademark:search.serialNumber')}: {result.serial_number}
                          {result.registration_number && ` | ${t('trademark:search.regNumber')}: ${result.registration_number}`}
                        </p>
                        {result.warning && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-2 flex items-center">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                            {result.warning}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${getSimilarityColor(result.similarity_scores.overall)}`}>
                          {Math.round(result.similarity_scores.overall * 100)}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t('trademark:search.similarity')}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>{t('trademark:search.phonetic')}: {Math.round(result.similarity_scores.phonetic * 100)}%</span>
                      <span>{t('trademark:search.visual')}: {Math.round(result.similarity_scores.visual * 100)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Official Search Links - IMPORTANT */}
          {searchResults.official_search_links && searchResults.official_search_links.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start mb-3">
                <LinkIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                    {t('trademark:search.officialSearchLinksTitle')}
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                    {t('trademark:search.officialSearchLinksDesc')}
                  </p>
                </div>
              </div>
              <div className="space-y-3 ml-7">
                {searchResults.official_search_links.map((link, idx) => (
                  <div key={idx} className="border-l-2 border-blue-300 dark:border-blue-600 pl-3">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium flex items-center"
                    >
                      {link.name}
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </a>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{link.description}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5 italic">
                      {t('trademark:search.tip')}: {link.search_tip}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">{searchResults.disclaimer}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderClassesTab = () => (
    <div className="space-y-6">
      {/* Class Suggestion Tool */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('trademark:classes.suggestTitle')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:classes.describeGoods')}
            </label>
            <textarea
              value={classDescription}
              onChange={(e) => setClassDescription(e.target.value)}
              placeholder={t('trademark:classes.describePlaceholder')}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSuggestClasses}
            disabled={loading || !classDescription.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            )}
            {t('trademark:classes.suggestButton')}
          </button>
        </div>

        {/* Suggested Classes */}
        {suggestedClasses.length > 0 && (
          <div className="mt-6 space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('trademark:classes.suggestedClasses')}
            </h4>
            {suggestedClasses.map((cls) => (
              <div
                key={cls.class_number}
                className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-blue-800 dark:text-blue-200">
                    {t('trademark:classes.classLabel')} {cls.class_number}: {cls.name}
                  </span>
                  {cls.relevance && (
                    <span className="text-xs px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                      {cls.relevance}
                    </span>
                  )}
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{cls.description}</p>
                {cls.reason && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 italic">{cls.reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Classes Browser */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowAllClasses(!showAllClasses)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {t('trademark:classes.browseAll')}
          </span>
          {showAllClasses ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
        {showAllClasses && (
          <div className="border-t border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
            {allClasses.map((cls) => (
              <div
                key={cls.class_number}
                className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {t('trademark:classes.classLabel')} {cls.class_number}: {cls.name}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{cls.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderSimilarityTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('trademark:similarity.title')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('trademark:similarity.description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:similarity.mark1')}
            </label>
            <input
              type="text"
              value={mark1}
              onChange={(e) => setMark1(e.target.value)}
              placeholder={t('trademark:similarity.mark1Placeholder')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:similarity.mark2')}
            </label>
            <input
              type="text"
              value={mark2}
              onChange={(e) => setMark2(e.target.value)}
              placeholder={t('trademark:similarity.mark2Placeholder')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <button
          onClick={handleAnalyzeSimilarity}
          disabled={loading || !mark1.trim() || !mark2.trim()}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {loading ? (
            <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <ScaleIcon className="h-5 w-5 mr-2" />
          )}
          {t('trademark:similarity.analyzeButton')}
        </button>
      </div>

      {/* Similarity Results */}
      {similarityResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('trademark:similarity.resultsTitle')}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {['phonetic', 'visual', 'conceptual', 'overall'].map((type) => (
              <div key={type} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className={`text-2xl font-bold ${getSimilarityColor(similarityResult.similarity_scores[type])}`}>
                  {Math.round(similarityResult.similarity_scores[type] * 100)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                  {t(`trademark:similarity.${type}`)}
                </div>
              </div>
            ))}
          </div>
          <div className={`p-4 rounded-lg ${
            similarityResult.likelihood_of_confusion === 'high'
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              : similarityResult.likelihood_of_confusion === 'moderate'
              ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
              : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          }`}>
            <h5 className={`font-semibold ${
              similarityResult.likelihood_of_confusion === 'high'
                ? 'text-red-800 dark:text-red-200'
                : similarityResult.likelihood_of_confusion === 'moderate'
                ? 'text-yellow-800 dark:text-yellow-200'
                : 'text-green-800 dark:text-green-200'
            }`}>
              {t(`trademark:similarity.likelihood.${similarityResult.likelihood_of_confusion}`)}
            </h5>
            <p className="text-sm mt-2">{similarityResult.recommendation}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderApplicationTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('trademark:application.title')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.mark')} *
            </label>
            <input
              type="text"
              value={applicationForm.mark}
              onChange={(e) => setApplicationForm({ ...applicationForm, mark: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.markType')}
            </label>
            <select
              value={applicationForm.mark_type}
              onChange={(e) => setApplicationForm({ ...applicationForm, mark_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="standard">{t('trademark:application.types.standard')}</option>
              <option value="stylized">{t('trademark:application.types.stylized')}</option>
              <option value="design">{t('trademark:application.types.design')}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.goodsServices')} *
            </label>
            <textarea
              value={applicationForm.goods_services}
              onChange={(e) => setApplicationForm({ ...applicationForm, goods_services: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.classes')} *
            </label>
            <input
              type="text"
              value={applicationForm.classes.join(', ')}
              onChange={(e) => {
                const classes = e.target.value.split(',').map((c) => parseInt(c.trim())).filter((c) => !isNaN(c));
                setApplicationForm({ ...applicationForm, classes });
              }}
              placeholder="9, 35, 42"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.useBasis')}
            </label>
            <select
              value={applicationForm.use_basis}
              onChange={(e) => setApplicationForm({ ...applicationForm, use_basis: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="intent_to_use">{t('trademark:application.basis.intentToUse')}</option>
              <option value="use_in_commerce">{t('trademark:application.basis.useInCommerce')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.ownerName')}
            </label>
            <input
              type="text"
              value={applicationForm.owner_name}
              onChange={(e) => setApplicationForm({ ...applicationForm, owner_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('trademark:application.ownerType')}
            </label>
            <select
              value={applicationForm.owner_type}
              onChange={(e) => setApplicationForm({ ...applicationForm, owner_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="individual">{t('trademark:application.ownerTypes.individual')}</option>
              <option value="corporation">{t('trademark:application.ownerTypes.corporation')}</option>
              <option value="llc">{t('trademark:application.ownerTypes.llc')}</option>
              <option value="partnership">{t('trademark:application.ownerTypes.partnership')}</option>
            </select>
          </div>
        </div>
        <button
          onClick={handleGetApplicationGuidance}
          disabled={loading || !applicationForm.mark || !applicationForm.goods_services || applicationForm.classes.length === 0}
          className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {loading ? (
            <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <DocumentTextIcon className="h-5 w-5 mr-2" />
          )}
          {t('trademark:application.getGuidance')}
        </button>
      </div>

      {/* Application Guidance Results */}
      {applicationData && (
        <div className="space-y-4">
          {/* Fees */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
            <h4 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
              {t('trademark:application.estimatedFees')}
            </h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-emerald-700 dark:text-emerald-300">{t('trademark:application.perClass')}:</span>
                <span className="font-bold ml-2">${applicationData.fees.per_class}</span>
              </div>
              <div>
                <span className="text-emerald-700 dark:text-emerald-300">{t('trademark:application.totalClasses')}:</span>
                <span className="font-bold ml-2">{applicationData.fees.total_classes}</span>
              </div>
              <div>
                <span className="text-emerald-700 dark:text-emerald-300">{t('trademark:application.totalFee')}:</span>
                <span className="font-bold ml-2">${applicationData.fees.total_fee}</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('trademark:application.timeline')}
            </h4>
            <div className="space-y-3">
              {applicationData.timeline.map((step: any, idx: number) => (
                <div key={idx} className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold text-sm">
                    {step.step}
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{step.action}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{step.timeframe}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Useful Links */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">
              {t('trademark:application.usefulLinks')}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(applicationData.useful_links).map(([key, url]) => (
                <a
                  key={key}
                  href={url as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-blue-700 dark:text-blue-300 hover:underline"
                >
                  <LinkIcon className="h-4 w-4 mr-1" />
                  {t(`trademark:links.${key}`)}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderFeesTab = () => (
    <div className="space-y-6">
      {loading && !feesData ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : feesData ? (
        <>
          {/* Application Fees */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('trademark:fees.applicationFees')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {feesData.application_fees.map((fee, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{fee.type}</span>
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-400">${fee.per_class}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{fee.description}</p>
                  <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    {fee.requirements.map((req, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-1">•</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Fees */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('trademark:fees.additionalFees')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {feesData.additional_fees.map((fee, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <span className="text-gray-700 dark:text-gray-300">{fee.action}</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">${fee.fee}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
              {t('trademark:fees.importantNotes')}
            </h4>
            <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
              {feesData.notes.map((note, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="mr-2">•</span>
                  {note}
                </li>
              ))}
            </ul>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
              {t('trademark:fees.lastUpdated')}: {feesData.last_updated}
            </p>
          </div>
        </>
      ) : null}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('trademark:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {t('trademark:subtitle')}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      {renderTabs()}

      {/* Tab Content */}
      {activeTab === 'search' && renderSearchTab()}
      {activeTab === 'classes' && renderClassesTab()}
      {activeTab === 'similarity' && renderSimilarityTab()}
      {activeTab === 'application' && renderApplicationTab()}
      {activeTab === 'fees' && renderFeesTab()}
    </div>
  );
};
